import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Favorite as FavoriteIcon,
  Movie as MovieIcon,
  Whatshot as WhatshotIcon,
  AttachMoney as AttachMoneyIcon,
  LocalMovies as LocalMoviesIcon
} from '@mui/icons-material';
import { brandColors } from '../../theme/theme';
import { getFilmsReleasingThisWeek } from '../../modules/FilmTracking/services/filmTrackingService';
import { formatDateForDisplay } from '../../lib/utils/dateUtils';
import { useFilmSubscription } from '../../hooks/useFilmSubscription';
import FilmTile from './FilmTile';
import boxOfficeProjectionSummaryService from '../../lib/services/boxOfficeProjectionSummaryService';

/**
 * ThisWeekWidget Component
 * 
 * Displays films releasing in the next 7 days
 * Available to all logged-in users (Film Free and above)
 */
const ThisWeekWidget = () => {
  const navigate = useNavigate();
  const { canAccessDashboard, forceUnlocked } = useFilmSubscription();
  const canNavigate = forceUnlocked || canAccessDashboard;
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectionSummaries, setProjectionSummaries] = useState({});
  const latestProjectionRequestRef = useRef(0);
  const fetchFilms = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getFilmsReleasingThisWeek();
      const filmsData = data.films || [];
      setFilms(filmsData);
      loadProjectionSummaries(filmsData);
    } catch (err) {
      console.error('Error fetching this week\'s films:', err);
      setError('Failed to load this week\'s releases');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectionSummaries = async (filmList) => {
    const filmIds = Array.from(
      new Set(
        (filmList || [])
          .map((film) => film.imdb_id || film.tt_id)
          .filter((id) => typeof id === 'string' && id.startsWith('tt'))
      )
    );

    const requestId = Date.now();
    latestProjectionRequestRef.current = requestId;

    if (filmIds.length === 0) {
      setProjectionSummaries({});
      return;
    }

    try {
      const summaries = await boxOfficeProjectionSummaryService.getProjectionSummaries(filmIds);
      if (latestProjectionRequestRef.current !== requestId) {
        return;
      }
      const projectionMap = {};
      filmIds.forEach((id) => {
        projectionMap[id] = summaries[id] || null;
      });
      setProjectionSummaries(projectionMap);
    } catch (projectionError) {
      console.error('Failed to load projection summaries (widget):', projectionError);
      if (latestProjectionRequestRef.current === requestId) {
        setProjectionSummaries({});
      }
    }
  };

  useEffect(() => {
    fetchFilms();
  }, []);

  const getMetricColor = (type) => {
    switch (type) {
      case 'awareness':
        return brandColors.awareness || '#1976d2';
      case 'interest':
        return brandColors.highInterest || '#d32f2f';
      case 'theater':
        return brandColors.theaterIntent || '#388e3c';
      case 'heat':
        return '#ff5722';
      case 'pay':
        return brandColors.willingToPay || '#f57c00';
      case 'plf':
        return '#9c27b0';
      default:
        return brandColors.primary;
    }
  };

  if (loading) {
    return (
      <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.primary }}>
              Domestic Tracking This Week
            </Typography>
          </Box>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.primary }}>
              Domestic Tracking This Week
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchFilms} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (films.length === 0) {
    return (
      <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.primary }}>
              Domestic Tracking This Week
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchFilms} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <MovieIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No films releasing this week
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      elevation={2} 
      sx={{ 
        mb: 4,
        border: `2px solid ${brandColors.primary}20`,
        borderRadius: 2
      }}
    >
      <CardContent>
        {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.primary }}>
              Domestic Tracking This Week
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Films releasing in the next 7 days
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchFilms} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Films Grid */}
        <Grid container spacing={3}>
          {films.map((film) => {
            const normalizePercentage = (value) => {
              if (value === null || value === undefined) return 0;
              if (Number.isNaN(Number(value))) return 0;
              return Math.min(Math.max(Number(value), 0), 100);
            };

            const safePercentage = (value) => {
              if (value === null || value === undefined || Number.isNaN(Number(value))) {
                return null;
              }
              return normalizePercentage(value);
            };

            const normalizeChange = (value) => {
              if (value === null || value === undefined || Number.isNaN(Number(value))) {
                return null;
              }
              return Number(value);
            };

            const awarenessSource = film.pct_aware ?? film.awareness_pct;
            const interestSource = film.pct_interest ?? film.high_interest_pct;
            const theaterSource = film.pct_theater ?? film.theater_intent_pct;

            const awareness = normalizePercentage(awarenessSource);
            const interest = normalizePercentage(interestSource);
            const theater = normalizePercentage(theaterSource);
            const heatSource = film.heat_pct ?? film.heat_score ?? film.heat;
            const willingnessSource = film.willingness_to_pay_pct ?? film.pay_to_see_pct ?? film.premium_willingness_pct;
            const plfSource = film.plf_pct ?? film.premium_format_pct;
            const heat = safePercentage(heatSource);
            const willingness = safePercentage(willingnessSource);
            const plf = safePercentage(plfSource);

            const metrics = [];

            const addMetric = ({ key, title, value, subtitle, icon, color, change }) => {
              if (value === null) return;
              metrics.push({
                key,
                title,
                value,
                subtitle,
                icon,
                color,
                change,
                suffix: '%'
              });
            };

            addMetric({
              key: 'awareness',
              title: 'Awareness',
              value: awareness,
              subtitle: 'Know about film',
              icon: <VisibilityIcon fontSize="small" />,
              color: getMetricColor('awareness'),
              change: normalizeChange(film.awareness_change)
            });

            addMetric({
              key: 'interest',
              title: 'Interest',
              value: interest,
              subtitle: 'Want to see',
              icon: <FavoriteIcon fontSize="small" />,
              color: getMetricColor('interest'),
              change: normalizeChange(film.high_interest_change)
            });

            addMetric({
              key: 'theater',
              title: 'Theater Intent',
              value: theater,
              subtitle: 'Plan to see in theaters',
              icon: <MovieIcon fontSize="small" />,
              color: getMetricColor('theater'),
              change: normalizeChange(film.theater_intent_change)
            });

            addMetric({
              key: 'heat',
              title: 'Heat Score',
              value: heat,
              subtitle: 'Overall buzz',
              icon: <WhatshotIcon fontSize="small" />,
              color: getMetricColor('heat'),
              change: normalizeChange(film.heat_change)
            });

            addMetric({
              key: 'willingness_to_pay',
              title: 'Willing to Pay',
              value: willingness,
              subtitle: 'Ready to spend',
              icon: <AttachMoneyIcon fontSize="small" />,
              color: getMetricColor('pay'),
              change: normalizeChange(film.willingness_to_pay_change)
            });

            addMetric({
              key: 'plf',
              title: 'Premium Format',
              value: plf,
              subtitle: 'PLF interest',
              icon: <LocalMoviesIcon fontSize="small" />,
              color: getMetricColor('plf'),
              change: normalizeChange(film.plf_change)
            });

            const footerLines = [];

            if (film.total_responses) {
              footerLines.push(`${film.total_responses.toLocaleString()} survey responses`);
            }

            const surveyDateRaw = film.survey_complete_date || film.survey_date;
            const surveyDisplay = film.survey_period || (surveyDateRaw ? formatDateForDisplay(surveyDateRaw) : null);
            if (surveyDisplay) {
              footerLines.push(`Survey completed ${surveyDisplay}`);
            }

            if (footerLines.length === 0) {
              footerLines.push('Domestic tracking snapshot');
            }

            const projectionSummary = projectionSummaries[film.imdb_id || film.tt_id];

            const tileFilm = {
              ...film,
              imdb_title: film.imdb_title || film.film_title,
              film_title: film.film_title || film.imdb_title,
              release_date_display: film.release_date_display || (film.release_date ? formatDateForDisplay(film.release_date) : null)
            };

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={film.tt_id || film.imdb_id || film.film_title}
              >
                <FilmTile
                  film={tileFilm}
                  metrics={metrics}
                  footerLines={footerLines}
                  allowNavigation={canNavigate}
                  onClick={() => {
                    if (canNavigate && (film.tt_id || film.imdb_id)) {
                      navigate(`/film-tracking/${film.tt_id || film.imdb_id}`);
                    }
                  }}
                  showSurveyDateInHeader={false}
                  projectionSummary={projectionSummary}
                />
              </Grid>
            );
          })}
        </Grid>

        {/* Info Note */}
        <Box sx={{ mt: 3, p: 2, bgcolor: `${brandColors.primary}08`, borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            ℹ️ Data shown is informational only. Upgrade to Film Basic to access the full Film Dashboard with filters and detailed insights.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ThisWeekWidget;

